import { type Room, Track } from "livekit-client";
import { useEffect, useState } from "react";

/** Optional visual meter. Reads the already-published mic; never captures or plays audio. */
export function useListeningMicLevel(room: Room | null | undefined, enabled: boolean) {
  const [level, setLevel] = useState<number | null>(null);

  useEffect(() => {
    setLevel(null);
    if (!enabled || !room) return;

    let context: AudioContext | null = null;
    let source: MediaStreamAudioSourceNode | null = null;
    let analyser: AnalyserNode | null = null;
    let samples: Uint8Array<ArrayBuffer> | null = null;
    let currentTrack: MediaStreamTrack | null = null;
    let failedTrack: MediaStreamTrack | null = null;
    let timer: ReturnType<typeof setInterval> | undefined;
    let smoothed = 0;

    const release = () => {
      source?.disconnect();
      analyser?.disconnect();
      if (context) void context.close().catch(() => undefined);
      context = null;
      source = null;
      analyser = null;
      samples = null;
      currentTrack = null;
      smoothed = 0;
      // The shared microphone track belongs to the session. Never stop it here.
    };

    const sample = () => {
      const publication = room.localParticipant.getTrackPublication(Track.Source.Microphone);
      const track = publication?.track?.mediaStreamTrack;
      if (
        room.state !== "connected" ||
        publication?.isMuted ||
        !track ||
        !track.enabled ||
        track.muted ||
        track.readyState !== "live"
      ) {
        release();
        setLevel(null);
        return;
      }
      if (track === failedTrack) return;

      try {
        if (track !== currentTrack) {
          release();
          const AudioContextClass =
            window.AudioContext ||
            (window as typeof window & { webkitAudioContext?: typeof AudioContext })
              .webkitAudioContext;
          if (!AudioContextClass) {
            failedTrack = track;
            setLevel(null);
            return;
          }
          context = new AudioContextClass();
          currentTrack = track;
          source = context.createMediaStreamSource(new MediaStream([track]));
          analyser = context.createAnalyser();
          analyser.fftSize = 512;
          source.connect(analyser);
          samples = new Uint8Array(analyser.fftSize);
          if (context.state === "suspended") void context.resume().catch(() => undefined);
        }
        if (context?.state !== "running" || !analyser || !samples) {
          setLevel(null);
          return;
        }
        analyser.getByteTimeDomainData(samples);
        let sum = 0;
        for (const value of samples) sum += ((value - 128) / 128) ** 2;
        const rms = Math.sqrt(sum / samples.length);
        const target = Math.min(1, Math.max(0, (rms - 0.012) / 0.16));
        smoothed += (target - smoothed) * (target > smoothed ? 0.45 : 0.2);
        setLevel(Math.round(smoothed * 100) / 100);
      } catch {
        // Meter support is optional. Keep the normal Listening indicator on failure.
        failedTrack = track;
        release();
        setLevel(null);
      }
    };

    const syncVisibility = () => {
      clearInterval(timer);
      release();
      setLevel(null);
      if (!document.hidden) {
        sample();
        timer = setInterval(sample, 100);
      }
    };
    syncVisibility();
    document.addEventListener("visibilitychange", syncVisibility);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", syncVisibility);
      release();
    };
  }, [room, enabled]);

  return enabled ? level : null;
}
